import { isEmptyValue } from "../shared/values";

import type {
  RunDetailOut,
  RunDiscrepancyOut,
  RunMatchedFieldOut,
  ScoreBucketOut,
} from "@platform/api/golden";

export type ScoreCategory = ScoreBucketOut["category"];

export const ACCURACIES: readonly {
  readonly category: ScoreCategory;
  readonly label: string;
}[] = [
  { category: "plan_design", label: "Plan design accuracy" },
  { category: "rate", label: "Rate accuracy" },
  { category: "other", label: "Other accuracy" },
];

export type DiscrepancyKind = "disagreed" | "rumi_only" | "analyst_only";

export const DISCREPANCY_KINDS: readonly {
  readonly kind: DiscrepancyKind;
  readonly label: string;
  readonly none: string;
}[] = [
  { kind: "disagreed", label: "Disagreed fields", none: "No disagreed fields" },
  { kind: "rumi_only", label: "RUMI-only fields", none: "No RUMI-only fields" },
  {
    kind: "analyst_only",
    label: "Analyst-only fields",
    none: "No analyst-only fields",
  },
];

/**
 * Who filled a discrepancy's field: RUMI and the analyst both (and disagree), RUMI
 * alone, or the analyst alone. The analyst's side is the expected value, as in the
 * score's counts.
 */
export const discrepancyKind = ({
  field_result: result,
}: RunDiscrepancyOut): DiscrepancyKind => {
  if (isEmptyValue(result.expected_value)) return "rumi_only";
  if (isEmptyValue(result.rumi_value)) return "analyst_only";
  return "disagreed";
};

export interface ScoreTotals {
  readonly filled: number;
  readonly filledCorrect: number;
  readonly bothFilled: number;
  readonly bothFilledCorrect: number;
  readonly blank: number;
  readonly blankExpected: number;
  readonly disagreed: number;
  readonly ambiguous: number;
  readonly pendingJudge: number;
}

const ZERO: ScoreTotals = {
  filled: 0,
  filledCorrect: 0,
  bothFilled: 0,
  bothFilledCorrect: 0,
  blank: 0,
  blankExpected: 0,
  disagreed: 0,
  ambiguous: 0,
  pendingJudge: 0,
};

/** The score's counts, summed over the buckets a filter keeps. */
export const totalOf = (buckets: readonly ScoreBucketOut[]): ScoreTotals =>
  buckets.reduce<ScoreTotals>(
    (sum, bucket) => ({
      filled: sum.filled + (bucket.filled ?? 0),
      filledCorrect: sum.filledCorrect + (bucket.filled_correct ?? 0),
      bothFilled: sum.bothFilled + (bucket.both_filled ?? 0),
      bothFilledCorrect:
        sum.bothFilledCorrect + (bucket.both_filled_correct ?? 0),
      blank: sum.blank + (bucket.blank ?? 0),
      blankExpected: sum.blankExpected + (bucket.blank_expected ?? 0),
      disagreed: sum.disagreed + (bucket.disagreed ?? 0),
      ambiguous: sum.ambiguous + bucket.ambiguous,
      pendingJudge: sum.pendingJudge + bucket.pending_judge,
    }),
    ZERO,
  );

export interface CoverageFilter {
  /** The coverage asked for in the URL; empty for all. */
  readonly requested: string;
  readonly excludeNew: boolean;
}

export interface CoverageView {
  /** The coverages to choose from. */
  readonly coverages: readonly string[];
  /** The coverage applied; empty for all. */
  readonly coverage: string;
  readonly isNew: (coverage: string | null | undefined) => boolean;
  readonly buckets: readonly ScoreBucketOut[];
  readonly discrepancies: readonly RunDiscrepancyOut[];
  readonly matched: readonly RunMatchedFieldOut[];
}

/**
 * What the coverage filter keeps of a run. A coverage keeps only its own fields; the
 * fields outside every coverage (benefit classes, the RFP's own) count only when none
 * is chosen. Excluding new coverages drops the ones the analyst's submission quotes as
 * new business. A coverage the run has no fields for (a stale link, or one the new
 * filter hides) filters nothing, since honoring it would hide every field.
 */
export const applyCoverageFilter = (
  detail: RunDetailOut,
  filter: CoverageFilter,
): CoverageView => {
  const newCoverages = detail.new_coverages ?? [];
  const isNew = (coverage: string | null | undefined): boolean =>
    coverage != null && newCoverages.includes(coverage);
  const breakdown = detail.score?.breakdown ?? [];
  const coverages = [
    ...new Set(
      breakdown.flatMap((bucket) => (bucket.coverage ? [bucket.coverage] : [])),
    ),
  ]
    .filter((coverage) => !(filter.excludeNew && isNew(coverage)))
    .sort();
  const coverage = coverages.includes(filter.requested) ? filter.requested : "";
  const keep = (candidate: string | null | undefined): boolean =>
    !(filter.excludeNew && isNew(candidate)) &&
    (coverage === "" || candidate === coverage);

  return {
    coverages,
    coverage,
    isNew,
    buckets: breakdown.filter((bucket) => keep(bucket.coverage)),
    discrepancies: (detail.discrepancies ?? []).filter((item) =>
      keep(item.coverage),
    ),
    matched: (detail.matched ?? []).filter((item) => keep(item.coverage)),
  };
};
